import { useEffect, useState } from 'react';
import axios from 'axios';

const CACHE_TTL = 5 * 60 * 1000; // 5 min
const imgCache = {};

// TODO - should be common hook
export const useImage = (url, initialLoading = false) => {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(initialLoading);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!url) {
      return;
    }

    let cancelled = false;
    let entry = imgCache[url];

    if (entry) {
      entry.subscribers = (entry.subscribers || 0) + 1;
      entry.cleanupTimeout = entry.cleanupTimeout || null;
      entry.controller = entry.controller || null;
      entry.promise = entry.promise || null;
      entry.resolvePromise = entry.resolvePromise || null;
      entry.rejectPromise = entry.rejectPromise || null;

      if (entry.cleanupTimeout) {
        clearTimeout(entry.cleanupTimeout);
        entry.cleanupTimeout = null;
      }

      if (entry.loading && entry.promise) {
        entry.promise
          .then(() => {
            if (cancelled) {
              return;
            }

            setSrc(entry.src);
            setLoading(false);
            setError(false);
          })
          .catch(() => {
            if (cancelled) {
              return;
            }

            setSrc(null);
            setLoading(false);
            setError(true);
          });
      }

      setSrc(entry.src);
      setLoading(entry.loading);
      setError(entry.error);
    } else {
      let resolvePromise = null;
      let rejectPromise = null;

      const promise = new Promise((resolve, reject) => {
        resolvePromise = resolve;
        rejectPromise = reject;
      });

      const controller = new AbortController();

      entry = {
        src: null,
        loading: true,
        error: false,
        subscribers: 1,
        cleanupTimeout: null,
        controller,
        promise,
        resolvePromise,
        rejectPromise,
      };

      imgCache[url] = entry;

      setSrc(null);
      setLoading(true);
      setError(false);

      axios(url, {
        responseType: 'blob',
        signal: controller.signal,
      })
        .then(response => {
          const image = URL.createObjectURL(response.data);

          entry.src = image;
          entry.loading = false;
          entry.error = false;
          
          if (entry.resolvePromise) {
            entry.resolvePromise();
          }

          if (!cancelled) {
            setSrc(image);
            setLoading(false);
            setError(false);
          }
        })
        .catch(error => {
          if (axios.isCancel(error)) {
            if (imgCache[url] === entry) {
              delete imgCache[url];
            }

            return;
          }

          entry.src = null;
          entry.loading = false;
          entry.error = true;
          
          if (entry.rejectPromise) {
            entry.rejectPromise(error);
          }

          if (!cancelled) {
            setSrc(null);
            setLoading(false);
            setError(true);
          }
        });
    }

    return () => {
      cancelled = true;

      if (imgCache[url] !== entry) {
        return;
      }

      entry.subscribers -= 1;

      if (entry.subscribers > 0) {
        return;
      }

      entry.cleanupTimeout = setTimeout(() => {
        if (entry.subscribers > 0 || imgCache[url] !== entry) {
          return;
        }

        if (entry.loading) {
          entry.controller?.abort();
        }

        if (entry.src) {
          URL.revokeObjectURL(entry.src);
        }

        delete imgCache[url];
      }, CACHE_TTL);
    };
  }, [url]);

  return { src, loading, error };
};

export const useImages = urls => {
  const [images, setImages] = useState({});

  useEffect(() => {
    urls.forEach(url => {
      if (imgCache[url]) {
        setImages(rest => ({ ...rest, [url]: imgCache[url] }));
        return;
      }

      setImages(rest => ({ ...rest, [url]: { src: null, loading: true, error: false } }));
      imgCache[url] = { src: null, loading: true, error: false };

      axios(url, { responseType: 'blob' })
        .then(response => {
          let image = URL.createObjectURL(response.data);
          setImages(rest => ({ ...rest, [url]: { src: image, loading: false, error: false } }));
          imgCache[url] = { src: image, loading: false, error: false };
        })
        .catch(() => {
          setImages(rest => ({ ...rest, [url]: { src: null, loading: false, error: true } }));
          imgCache[url] = { src: null, loading: false, error: true };
        });
    });
  }, [urls]);

  return Object.values(images);
};
